import { useLanguage } from "@/components/language-provider";
import { useState, useEffect } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Building2, Crown, Star, Zap, Check, X } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { apiRequest } from "@/lib/queryClient";
import { API_BASE_URL } from "@/lib/config";
import { BrandLogo } from "@/components/brand-logo";
import { useQuery } from "@tanstack/react-query";
import { PLANS, planRequiresPayment } from "@/lib/plans";
import { formatCpf, isCpfChecksumValid, normalizeCpf, type CpfStatus } from "@/lib/cpf";

interface Country {
  code: string;
  name: string;
  flag: string;
}

export default function Register() {
  const { t, language } = useLanguage();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [cpf, setCpf] = useState("");
  const [cpfStatus, setCpfStatus] = useState<CpfStatus>("idle");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptElectronicSignature, setAcceptElectronicSignature] = useState(false);
  
  // Get selected plan from URL
  const urlParams = new URLSearchParams(searchString);
  const selectedPlan = urlParams.get("plan") || "free";
  const selectedCycle = urlParams.get("cycle") === "annual" ? "annual" : "monthly";
  const planInfo = PLANS[selectedPlan as keyof typeof PLANS] || PLANS.free;
  const isPaidPlan = planRequiresPayment(selectedPlan);

  // Plan name/price shown in the badge come from the API (managed in the admin);
  // the static PLANS entry is only the fallback when the API is unavailable.
  const [apiPlan, setApiPlan] = useState<{ name: string; priceMonthlyBrl: number; priceMonthlyUsd: number; contactSales: boolean } | null>(null);
  useEffect(() => {
    if (selectedPlan === "free") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/plans`);
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
        const found = list.find((p) => String(p.planType || "").toLowerCase() === selectedPlan.toLowerCase());
        if (!cancelled && found) setApiPlan(found);
      } catch {
        console.error("[FillSign] Could not load plans from the API — showing default plan values.");
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPlan]);
  const planName = apiPlan?.name ?? planInfo.name;
  const planPrice = apiPlan
    ? (apiPlan.contactSales ? (language === "pt" ? "Sob consulta" : "Custom") : `R$ ${apiPlan.priceMonthlyBrl}${language === "pt" ? "/mês" : "/mo"}`)
    : (planInfo.price.brl > 0 ? `R$ ${planInfo.price.brl}${language === "pt" ? "/mês" : "/mo"}` : (language === "pt" ? "Sob consulta" : "Custom"));

  const { data: countries = [] } = useQuery<Country[]>({
    queryKey: ["/api/auth/countries"],
  });

  const isBrazil = countryCode === "BR";
  const requiresIdentity = countryCode !== "";

  // Real-time CPF validation: client-side checksum first, then debounced
  // server check for uniqueness. Only runs when the user is on the BR flow
  // and has typed all 11 digits.
  useEffect(() => {
    if (!isBrazil) {
      setCpfStatus("idle");
      return;
    }

    const normalized = normalizeCpf(cpf);

    if (normalized.length === 0) {
      setCpfStatus("idle");
      return;
    }

    if (normalized.length < 11) {
      setCpfStatus("idle");
      return;
    }

    if (!isCpfChecksumValid(normalized)) {
      setCpfStatus("invalid_checksum");
      return;
    }

    setCpfStatus("checking");
    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/identity/cpf/check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cpf: normalized }),
          signal: controller.signal,
        });
        const json = await res.json();
        const data = json?.data ?? json;
        const valid = data?.valid ?? false;
        const available = data?.available ?? false;
        const reason = data?.reason as string | null | undefined;

        if (!valid) {
          setCpfStatus("invalid_checksum");
        } else if (!available) {
          setCpfStatus(reason === "already_in_use" ? "already_in_use" : "error");
        } else {
          setCpfStatus("valid");
        }
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        setCpfStatus("error");
      }
    }, 350);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [cpf, isBrazil]);

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCpf(formatCpf(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!acceptTerms || !acceptElectronicSignature) {
      toast({ 
        title: t("ui.consent_required"), 
        description: t("ui.you_must_accept_the_terms_of_service_and_electro_236"), 
        variant: "destructive" 
      });
      return;
    }

    if (!countryCode) {
      toast({ 
        title: t("ui.country_required"), 
        description: t("ui.please_select_your_country"), 
        variant: "destructive" 
      });
      return;
    }

    if (isBrazil && !cpf) {
      toast({ 
        title: t("ui.cpf_required"), 
        description: t("ui.cpf_is_required_for_brazilian_users"), 
        variant: "destructive" 
      });
      return;
    }

    if (isBrazil && cpfStatus !== "valid") {
      const messages: Record<CpfStatus, string> = {
        idle: "Digite um CPF válido",
        checking: "Aguarde a validação do CPF",
        valid: "",
        invalid_checksum: "CPF inválido. Verifique os números digitados.",
        already_in_use: "Este CPF já está cadastrado em outra conta",
        error: "Erro ao validar o CPF. Tente novamente.",
      };
      toast({
        title: t("ui.cpf_not_validated"),
        description: messages[cpfStatus],
        variant: "destructive",
      });
      return;
    }

    if (!isBrazil && !phoneNumber) {
      toast({ 
        title: t("ui.phone_required"), 
        description: t("ui.phone_number_is_required_for_identity_verificati"), 
        variant: "destructive" 
      });
      return;
    }
    
    setLoading(true);
    try {
      const res = await apiRequest("POST", "/api/auth/register", { 
        email, 
        password, 
        firstName, 
        lastName,
        acceptTerms,
        acceptElectronicSignature,
        countryCode,
        language: localStorage.getItem("language") || "pt",
        cpf: isBrazil ? cpf : undefined,
        phoneNumber: !isBrazil ? phoneNumber : undefined,
      });
      const data = await res.json();
      if (!res.ok) {
        // Surface CPF-specific errors back to the form for race-condition cases.
        const errMsg = data.message || data.errors?.[0]?.message || data.errors?.[0] || "Falha no cadastro";
        const lowerMsg = String(errMsg).toLowerCase();
        if (lowerMsg.includes("cpf")) {
          if (lowerMsg.includes("já está cadastrado") || lowerMsg.includes("ja esta cadastrado")) {
            setCpfStatus("already_in_use");
          } else {
            setCpfStatus("invalid_checksum");
          }
        }
        throw new Error(errMsg);
      }
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      
      // If paid plan selected, redirect to Stripe checkout
      if (isPaidPlan && data.user?.id) {
        try {
          const checkoutRes = await apiRequest("POST", "/api/v1/subscriptions/create-checkout-session", {
            planId: selectedPlan,
            billingCycle: selectedCycle,
            userId: data.user.id,
            successUrl: `${window.location.origin}/dashboard?subscription=success`,
            cancelUrl: `${window.location.origin}/dashboard?subscription=cancelled`,
          });
          const checkoutData = await checkoutRes.json();
          // Check for checkoutUrl in different response formats
          const checkoutUrl = checkoutData.checkoutUrl || checkoutData.data?.checkoutUrl || checkoutData.url;
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          } else {
            console.error("No checkout URL in response:", checkoutData);
            toast({ 
              title: t("ui.account_created"), 
              description: t("ui.your_account_was_created_please_upgrade_your_pla"),
            });
          }
        } catch (stripeError: any) {
          console.error("Stripe checkout error:", stripeError);
          // Continue to dashboard even if Stripe fails - they can upgrade later
          toast({ 
            title: t("ui.account_created"), 
            description: t("ui.your_account_was_created_you_can_upgrade_your_pl"),
          });
        }
      }
      
      if (data.needsPhoneVerification) {
        toast({ title: "Verify Your Phone", description: "Please verify your phone number to complete registration" });
        setLocation("/verify-phone");
      } else {
        toast({ title: "Account created", description: t("ui.welcome_to_fillsign") });
        setLocation("/dashboard");
      }
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setOauthLoading("google");
    try {
      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!googleClientId) {
        toast({ 
          title: t("ui.configuration_error"), 
          description: t("ui.google_oauth_is_not_configured_please_contact_su"), 
          variant: "destructive" 
        });
        setOauthLoading(null);
        return;
      }
      
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const scope = "openid email profile";
      // Use response_type=id_token token to get both tokens
      // nonce is required for id_token
      const nonce = Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem("oauth_nonce", nonce);
      // Store flag to indicate this is a registration flow
      sessionStorage.setItem("oauth_register", "true");
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${googleClientId}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=id_token token` +
        `&scope=${encodeURIComponent(scope)}` +
        `&nonce=${nonce}`;
      
      window.location.href = authUrl;
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
      setOauthLoading(null);
    }
  };

  const handleMicrosoftLogin = async () => {
    setOauthLoading("microsoft");
    try {
      const msClientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
      if (!msClientId) {
        toast({ 
          title: t("ui.configuration_error"), 
          description: t("ui.microsoft_oauth_is_not_configured_please_contact"), 
          variant: "destructive" 
        });
        return;
      }
      
      const redirectUri = `${window.location.origin}/auth/microsoft/callback`;
      const scope = "openid email profile User.Read";
      const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${msClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}`;
      
      window.location.href = authUrl;
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setOauthLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link href="/" className="flex items-center justify-center mb-4">
            <BrandLogo />
          </Link>
          <CardTitle>{t("ui.create_your_account")}</CardTitle>
          <CardDescription>{t("ui.start_signing_documents_in_minutes")}</CardDescription>
          
          {/* Selected Plan Badge */}
          {selectedPlan !== "free" && (
            <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center justify-center gap-2">
                {selectedPlan === "pro" && <Star className="w-5 h-5 text-blue-500" />}
                {selectedPlan === "enterprise" && <Crown className="w-5 h-5 text-purple-500" />}
                <span className="font-medium">{language === "pt" ? "Plano" : "Plan"}: {planName}</span>
                <Badge variant="secondary" className="ml-2">{planPrice}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isPaidPlan
                  ? (language === "pt" ? "Você será direcionado ao pagamento após o cadastro" : "You'll be redirected to payment after registration")
                  : (language === "pt" ? "Plano gratuito selecionado" : "Free plan selected")}
              </p>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="outline" 
              onClick={handleGoogleLogin}
              disabled={oauthLoading !== null}
              data-testid="button-google-register"
            >
              {oauthLoading === "google" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <SiGoogle className="mr-2 h-4 w-4" />
              )}
              Google
            </Button>
            <Button 
              variant="outline" 
              onClick={handleMicrosoftLogin}
              disabled={oauthLoading !== null}
              data-testid="button-microsoft-register"
            >
              {oauthLoading === "microsoft" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Building2 className="mr-2 h-4 w-4" />
              )}
              Microsoft
            </Button>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t("ui.or_continue_with_email")}</span>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t("ui.first_name")}</Label>
                <Input
                  id="firstName"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="input-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t("ui.last_name_111")}</Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  data-testid="input-lastname"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("ui.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("ui.password")}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t("ui.create_a_password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="input-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="country">{t("ui.country")}</Label>
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger data-testid="select-country">
                  <SelectValue placeholder={t("ui.select_your_country")} />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("ui.required_for_identity_verification")}
              </p>
            </div>

            {isBrazil && (
              <div className="space-y-2">
                <Label htmlFor="cpf">CPF</Label>
                <div className="relative">
                  <Input
                    id="cpf"
                    type="text"
                    placeholder="000.000.000-00"
                    value={cpf}
                    onChange={handleCpfChange}
                    maxLength={14}
                    required
                    data-testid="input-cpf"
                    className={
                      cpfStatus === "valid"
                        ? "pr-10 border-green-500 focus-visible:ring-green-500"
                        : cpfStatus === "invalid_checksum" || cpfStatus === "already_in_use" || cpfStatus === "error"
                        ? "pr-10 border-red-500 focus-visible:ring-red-500"
                        : "pr-10"
                    }
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {cpfStatus === "checking" && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {cpfStatus === "valid" && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                    {(cpfStatus === "invalid_checksum" ||
                      cpfStatus === "already_in_use" ||
                      cpfStatus === "error") && (
                      <X className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                </div>
                {cpfStatus === "idle" && (
                  <p className="text-xs text-muted-foreground">
                    {t("ui.your_cpf_is_used_to_prevent_duplicate_accounts")}
                  </p>
                )}
                {cpfStatus === "checking" && (
                  <p className="text-xs text-muted-foreground">Validando CPF...</p>
                )}
                {cpfStatus === "valid" && (
                  <p className="text-xs text-green-600">CPF válido</p>
                )}
                {cpfStatus === "invalid_checksum" && (
                  <p className="text-xs text-red-600">CPF inválido. Verifique os números digitados.</p>
                )}
                {cpfStatus === "already_in_use" && (
                  <p className="text-xs text-red-600">Este CPF já está cadastrado em outra conta.</p>
                )}
                {cpfStatus === "error" && (
                  <p className="text-xs text-red-600">Erro ao validar o CPF. Tente novamente.</p>
                )}
              </div>
            )}

            {requiresIdentity && !isBrazil && (
              <div className="space-y-2">
                <Label htmlFor="phone">{t("ui.phone_number")}</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  data-testid="input-phone"
                />
                <p className="text-xs text-muted-foreground">
                  {t("ui.we_ll_send_a_verification_code_to_confirm_your_i")}
                </p>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="acceptTerms"
                  checked={acceptTerms}
                  onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                  data-testid="checkbox-terms"
                />
                <label htmlFor="acceptTerms" className="text-sm text-muted-foreground cursor-pointer leading-tight">
                  I agree to the{" "}
                  <Link href="/terms" className="text-primary hover:underline" target="_blank">
                    {t("ui.terms_of_service")}
                  </Link>{" "}
                  and{" "}
                  <Link href="/privacy" className="text-primary hover:underline" target="_blank">
                    {t("ui.privacy_policy")}
                  </Link>
                </label>
              </div>
              
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="acceptElectronicSignature"
                  checked={acceptElectronicSignature}
                  onCheckedChange={(checked) => setAcceptElectronicSignature(checked === true)}
                  data-testid="checkbox-electronic-signature"
                />
                <label htmlFor="acceptElectronicSignature" className="text-sm text-muted-foreground cursor-pointer leading-tight">
                  I consent to conduct business electronically and agree that my electronic signature has the same legal effect as a handwritten signature
                </label>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !acceptTerms || !acceptElectronicSignature || !countryCode || (isBrazil && cpfStatus !== "valid")} 
              data-testid="button-submit"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("ui.create_account")}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link href="/login" className="text-primary hover:underline" data-testid="link-login">
              {t("ui.sign_in_183")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
